import type { PumpPortalLiveRow } from "@/hooks/usePumpPortalTrades";

export interface SVIXSnapshot {
  timeSec: number;
  svix: number; // 0 to 100
  // internal metrics
  toxicity: number; 
  retailCrowding: number;
}

const SVIX_MAX = 100;
const SVIX_MIN = 0;

/**
 * Derives the Synthetic Volatility Index (SVIX) based on order flow stop-loss hunting
 * and retail crowding vs whale dumping dynamics.
 */
export class SVIXEngine {
  private lastSvix = 20; // base resting volatility
  private tradesWindow: PumpPortalLiveRow[] = [];
  private WINDOW_MS = 10000; // 10 seconds rolling window

  public processRow(row: PumpPortalLiveRow): SVIXSnapshot {
    this.tradesWindow.push(row);
    // filter window
    const cutoff = row.ts - this.WINDOW_MS;
    this.tradesWindow = this.tradesWindow.filter(t => t.ts >= cutoff);

    // Calculate metrics
    let sellPressure = 0;
    let buyPressure = 0;
    let retailBuys = 0;
    let whaleSells = 0;

    for (const t of this.tradesWindow) {
      if (t.buy) {
        buyPressure += t.sol;
        if (t.sol < 1) retailBuys++; // small buy = retail
      } else {
        sellPressure += t.sol;
        if (t.sol > 5) whaleSells++; // large sell = whale/hunt
      }
    }

    // Volatility spikes when there are rapid huge sells (liquidity grabs) 
    // OR when retail crowding is met by whale sells (hunt trap).
    
    // 1. Retail Crowding metric: High count of small buys
    const retailCrowding = Math.min(retailBuys / 20, 1); // 0 to 1

    // 2. Toxicity: Heavy unexpected selling pressure vs buying
    const toxicity = Math.min(whaleSells * 0.2 + (sellPressure / (buyPressure || 1)) * 0.1, 1);

    // 3. Spiky Spread Simulation (Theoretical slippage based on volatility)
    const turbulence = Math.abs(buyPressure - sellPressure) / ((buyPressure + sellPressure) || 1);

    // Base target SVIX calculation
    let targetSvix = 15 + (toxicity * 40) + (retailCrowding * 20) + (turbulence * 25);
    
    // Cap
    targetSvix = Math.max(SVIX_MIN, Math.min(targetSvix, SVIX_MAX));

    // Smooth movement towards target
    this.lastSvix = this.lastSvix + (targetSvix - this.lastSvix) * 0.2;

    return {
      timeSec: Math.floor(row.ts / 1000),
      svix: this.lastSvix,
      toxicity,
      retailCrowding
    };
  }
}
